// Sugalarm — app-wide settings context (cycle 14).
//
// Loads /api/settings once after the user signs in and exposes the
// current `UserSettings` plus a `refresh()` callback. Children that
// PATCH /api/settings should call `refresh()` after a successful PATCH
// so other screens immediately see the new values (units, thresholds,
// etc.).
//
// This is a deliberately tiny context — `useState` + `useContext`, no
// data-fetching library. Keep it that way; the cycle-9 tech lock-in
// rules out adding one.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import * as settingsApi from "../api/settings";
import { retryResult } from "./retryResult";
import type { UserSettings } from "./types";

export interface SettingsContextValue {
  settings: UserSettings | null;
  /** Re-fetches /api/settings. Call after a successful PATCH. */
  refresh: () => Promise<void>;
  /** Apply a settings payload arriving via WS (cycle 24). Idempotent —
   *  the incoming object becomes the new cached state regardless of
   *  who initiated the PATCH. */
  applyRemoteUpdate: (next: UserSettings) => void;
  /** Convenience accessor — defaults to "mg/dL" while settings are
   *  loading so callers don't have to null-check before formatting. */
  units: "mg/dL" | "mmol/L";
}

const SettingsCtx = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({
  enabled,
  accountKey = null,
  children,
}: {
  /** False while signed-out; the provider mounts but does not fetch. */
  enabled: boolean;
  /** Cycle 435 (security re-run, MEDIUM): the signed-in ACCOUNT's id.
   *  `/login` is routed inside the mounted shell, so a sign-in as a
   *  different account never unmounts this provider and never changes
   *  `enabled` — the previous account's thresholds and `alerts_enabled`
   *  stayed cached and judged the NEW account's glucose. `alerts_enabled`
   *  is the sharp end: a stale `false` suppressed the new account's low
   *  until the next `visibilitychange`. Clearing to null is the LOUD
   *  direction — `alerts_enabled !== false` re-enables alarms and
   *  `useThresholds` falls back to `DEFAULT_THRESHOLDS` — so the gap is
   *  covered by defaults, never by another account's numbers. */
  accountKey?: string | null;
  children: ReactNode;
}) {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const lastAccountRef = useRef<string | null>(accountKey);
  // Assigned during RENDER so it is always the newest key: `refresh`'s
  // deps are `[enabled]`, so its closed-over `accountKey` is stale and a
  // guard reading the prop would be a silent no-op.
  const accountKeyRef = useRef<string | null>(accountKey);
  accountKeyRef.current = accountKey;

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (!enabled) {
        setSettings(null);
        return;
      }
      const forAccount = accountKeyRef.current;
      // Cycle 76d: retry transient failures with backoff. Before this a
      // single mount fetch failing (e.g. the flaky Fly `ord` edge resets
      // seen in the field) left `settings` null forever — the Settings
      // pane sat on "Loading…" and the kiosk fell back to default
      // (bottom) nav until a manual reload.
      const r = await retryResult(() => settingsApi.get(), { signal });
      if (signal?.aborted) return;
      // Cycle 435: `refresh()` is also public and signal-less (every
      // screen calls it after a PATCH) and retries with backoff, so the
      // clear below cannot cancel it. A settings payload for an account
      // that is no longer signed in is dropped, never applied.
      if (accountKeyRef.current !== forAccount) return;
      if (r.ok) setSettings(r.data);
    },
    [enabled],
  );

  // Mount fetch + recovery listeners. Aborting on cleanup stops any
  // in-flight retry chain when the user signs out / the provider remounts.
  const recoverInFlight = useRef(false);
  useEffect(() => {
    // Cycle 435: a different account — drop the previous account's
    // thresholds and alarm-enable flag BEFORE the refetch. The cycle-90
    // member-switch re-pull in `App.tsx` cannot cover this: it requires
    // `prev && next && prev !== next`, and an account switch routes the
    // active member through null, satisfying it on neither hop.
    if (lastAccountRef.current !== accountKey) {
      lastAccountRef.current = accountKey;
      setSettings(null);
    }
    const ctrl = new AbortController();
    void refresh(ctrl.signal);

    if (!enabled) return () => ctrl.abort();

    const onRecover = () => {
      if (document.visibilityState === "hidden") return;
      if (recoverInFlight.current) return;
      recoverInFlight.current = true;
      void refresh(ctrl.signal).finally(() => {
        recoverInFlight.current = false;
      });
    };
    window.addEventListener("online", onRecover);
    document.addEventListener("visibilitychange", onRecover);
    return () => {
      ctrl.abort();
      window.removeEventListener("online", onRecover);
      document.removeEventListener("visibilitychange", onRecover);
    };
  }, [enabled, refresh, accountKey]);

  const applyRemoteUpdate = useCallback((next: UserSettings) => {
    setSettings(next);
  }, []);

  const value = useMemo<SettingsContextValue>(
    () => ({
      settings,
      refresh,
      applyRemoteUpdate,
      units: settings?.units ?? "mg/dL",
    }),
    [settings, refresh, applyRemoteUpdate],
  );

  return <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>;
}

/** Read the app-wide settings context. Throws if used outside the
 *  provider — that means a screen rendered before sign-in has tried
 *  to consume settings, which is a bug. */
export function useSettings(): SettingsContextValue {
  const v = useContext(SettingsCtx);
  if (!v) {
    throw new Error("useSettings called outside SettingsProvider");
  }
  return v;
}
